import React, { useState, useEffect, useRef } from 'react';
import { FaExclamationTriangle, FaPlus, FaRegEdit, FaRegTrashAlt } from 'react-icons/fa';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import AddLegacyModal from './addLegacy';
import { addGroup, deleteGroups, updateGroup } from '../../../../services/commonapi/commonApi';
import { getLegacyServices, addLegacy, updateLegacy, deleteLegacy } from '../../../../services/legacy/legacyApi';
import moment from 'moment';
import ConfirmDialog from '../../../../components/ConfirmDialog';


const ViewLegacy = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(10)
  const [page, setPage] = useState(0);
  const [groupsData, setGroupsData] = useState([]);
  const [type, setType] = useState('add');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({});

  const toast = useRef(null);
  const accessToken = JSON.parse(localStorage.getItem('access_token'));

  useEffect(() => {
    GetLegacyAllData();
  }, []);

  const openConfirmDialog = (config) => {
    setDialogConfig(config);
    setIsDialogOpen(true);
  };

  const confirmDelete = (user) => {
    openConfirmDialog({
      title: "Confirm Delete",
      message: `Are you sure you want to delete this legacy?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      cancelClass: "bg-primary hover:border-white text-gray-300 font-bold py-2 px-4 rounded border border-[rgb(161,147,147)]",
      confirmClass: "bg-secondary hover:bg-gray-400 font-bold py-2 px-4 rounded ml-4 text-primary border",
      onConfirm: () => handleDelete(user),
    });
  };


  const handleDelete = async (user) => {
    try {
      const accessToken = JSON.parse(localStorage.getItem('access_token'));
      const response = await deleteLegacy(accessToken, user?.id);
      if (response) {
        GetLegacyAllData()
        toast.current.show({ className: "bg-green-300 text-white", severity: 'success', summary: 'Success', detail: 'Legacy deleted successfully', life: 3000 });
      }
    } catch (error) {
      toast.current.show({ className: "bg-yellow-700 text-white", severity: 'warn', summary: 'Warning', detail: error.message, life: 3000 });
    }
  };

  const GetLegacyAllData = async () => {
    setLoading(true);
    try {
      const response = await getLegacyServices(accessToken);
      if (response.status === 200) {
        const data = response?.data.data;
        setGroupsData(data);
      } else {
        throw new Error('Failed to fetch user data');
      }
    } catch (err) {
      console.log(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const dateTemplate = (rowData) => {
    return rowData.created_at ? moment(rowData.created_at).format('DD-MM-YYYY') : "no data";
  };

  const actionBodyTemplate = (rowData) => {
    const iconClass = rowData?.is_default ? "text-gray-500 cursor-not-allowed" : "cursor-pointer";

    return (
      <div className="flex gap-2">
        <button disabled={rowData?.is_default} className="focus:outline-none text-secondary">
          <FaRegEdit
            className={iconClass}
            onClick={() => {
              if (!rowData?.is_default) {
                setIsModalOpen(true);
                setType("edit");
                setSelectedGroup(rowData);
              }
            }}
          />
        </button>
        <button disabled={rowData?.is_default} className="focus:outline-none text-expense">
          <FaRegTrashAlt
            className={iconClass}
            onClick={() => {
              if (!rowData?.is_default) {
                confirmDelete(rowData);
              }
            }}
          />
        </button>
      </div>
    );
  };


  const handleModalClose = () => {
    setIsModalOpen(false);
  };
  const handleAddLegacyClick = () => {
    setType("add")
    setIsModalOpen(true);
  };

  const handleSubmit = async ({ name, relation, onClose, setError, setLoading }) => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setLoading(true);
    try {

      const response = type === "add" ? await addLegacy(name, relation, accessToken) : await updateLegacy(name, relation, selectedGroup.id, accessToken);

      if (response.data.status === true) {
        GetLegacyAllData();
        toast.current.show({
          className: 'bg-green-300 text-white',
          severity: 'success',
          summary: 'Success',
          detail: response.data.message,
          life: 3000,
        });

        onClose();
      } else {
        toast.current.show({
          className: 'bg-yellow-700 text-white',
          severity: 'warn',
          summary: 'Warning',
          detail: response.data.message || 'Failed to add group',
          life: 3000,
        });
      }
    } catch (error) {
      toast.current.show({
        className: 'bg-red-700 text-white',
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'An unexpected error occurred',
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog
        isOpen={isDialogOpen}
        setIsOpen={setIsDialogOpen}
        {...dialogConfig}
      />

      <div className="p-4">

        <div className="flex justify-end mb-6 flex-col md:flex-row">
          <div className='flex justify-end'>
            <button
              type="submit"
              className="ml-6 inline-flex justify-center px-4 py-2 items-center gap-1 text-sm font-medium text-primary bg-secondary rounded-md hover:bg-gray-100 focus:outline-none"
              onClick={handleAddLegacyClick}
            >
              <FaPlus /> Add Legacy
            </button>
          </div>
        </div>
        <DataTable
          value={groupsData}
          stripedRows
          className="usertable"
          paginator
          rows={limit}
          totalRecords={groupsData.length}
          first={page * limit}
          onPage={(e) => {
            setPage(e.page);
            setLimit(e.rows);
          }}
          loading={loading}
          rowsPerPageOptions={[5, 10, 25, 50]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
        >
          <Column field="name" header="Name" sortable />
          <Column field="relation" header="Relation" />
          <Column field="created_at" header="Created Date" body={dateTemplate} />
          <Column field="Action" header="Action" body={actionBodyTemplate} />

        </DataTable>
        {isModalOpen && (
          <AddLegacyModal
            type={type}
            selectedGroup={selectedGroup}
            onClose={handleModalClose}
            handleSubmit={handleSubmit}
          />
        )}
      </div>
    </>
  );
};

export default ViewLegacy;
